import React from 'react';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Zap, Droplet, Ruler, ShieldCheck, UploadCloud, Plus, X, ArrowRight } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../lib/firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { DocumentDropzone } from '../components/DocumentDropzone';
import { FestivalCreationHeader } from '../components/FestivalCreationHeader';

interface PitchFormat {
  id: string;
  name: string;
  width: string;
  depth: string;
  flooring: string;
  price: string;
  hasElectricity: boolean;
}

export default function CreateFestivalStep3Page() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isSaving, setIsSaving] = useState(false);
  // Form State
  const [hasDrinkingWater, setHasDrinkingWater] = useState(true);
  const [wastewaterDetails, setWastewaterDetails] = useState('');
  
  const [providesTables, setProvidesTables] = useState(false);
  const [nightSecurity, setNightSecurity] = useState('');
  
  // New Planning Logistique Fields
  const [arrivalDate, setArrivalDate] = useState('');
  const [arrivalTime, setArrivalTime] = useState('');
  const [setupStartTime, setSetupStartTime] = useState('');
  const [setupEndTime, setSetupEndTime] = useState('');
  const [departureTime, setDepartureTime] = useState('');
  const [applicationDeadline, setApplicationDeadline] = useState('');

  // New Infrastructure Fields
  const [equipmentList, setEquipmentList] = useState<string[]>([]);

  // New Security & Logistics Fields
  const [parkingOption, setParkingOption] = useState('');
  const [securityOption, setSecurityOption] = useState('');
  const [wasteManagement, setWasteManagement] = useState('');
  
  useEffect(() => {
    const fetchFestival = async () => {
      if (!user) return;
      const activeId = sessionStorage.getItem('activeFestivalId') || user.uid;
      try {
        const docRef = doc(db, 'festivals', activeId);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const data = docSnap.data().technical || {};
          if (data.hasDrinkingWater !== undefined) setHasDrinkingWater(data.hasDrinkingWater);
          if (data.wastewaterDetails !== undefined) setWastewaterDetails(data.wastewaterDetails);
          if (data.providesTables !== undefined) setProvidesTables(data.providesTables);
          if (data.nightSecurity !== undefined) setNightSecurity(data.nightSecurity);
          
          if (data.arrivalDate) setArrivalDate(data.arrivalDate);
          if (data.arrivalTime) setArrivalTime(data.arrivalTime);
          if (data.setupStartTime) setSetupStartTime(data.setupStartTime);
          if (data.setupEndTime) setSetupEndTime(data.setupEndTime);
          if (data.departureTime) setDepartureTime(data.departureTime);
          if (data.applicationDeadline) setApplicationDeadline(data.applicationDeadline);
          if (data.equipmentList) setEquipmentList(data.equipmentList);
          if (data.parkingOption) setParkingOption(data.parkingOption);
          if (data.securityOption) setSecurityOption(data.securityOption);
          if (data.wasteManagement) setWasteManagement(data.wasteManagement);
        }
      } catch (error) {
        console.error("Error fetching technical details", error);
      }
    };
    fetchFestival();
  }, [user]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    setIsSaving(true);
    const activeId = sessionStorage.getItem('activeFestivalId') || user.uid;

    try {
      await setDoc(doc(db, 'festivals', activeId), {
        technical: {
          hasDrinkingWater,
          wastewaterDetails,
          providesTables,
          nightSecurity,
          arrivalDate,
          arrivalTime,
          setupStartTime,
          setupEndTime,
          departureTime,
          applicationDeadline,
          equipmentList,
          parkingOption,
          securityOption,
          wasteManagement
        }
      }, { merge: true });
      navigate('/create-festival/step-docs');
    } catch (error) {
      console.error("Error saving technical details", error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#121212] text-white font-sans relative overflow-x-hidden pb-24">
      {/* Header */}
      <FestivalCreationHeader onBack={() => navigate(-1)} />

      {/* Main Content */}
      <main className="relative z-30 max-w-4xl mx-auto px-4 md:px-6 pt-4">
        
        {/* Progress Bar */}
        <div className="mb-8 md:mb-12">
          <div className="flex items-center justify-between text-xs font-black text-gray-500 mb-4 uppercase tracking-[0.2em]">
            <span>Technique • Étape 6/7</span>
            <span className="text-[#FF5E00]">Logistique</span>
          </div>
          <div className="grid grid-cols-7 gap-1.5 md:gap-2">
            {[1, 2, 3, 4, 5, 6, 7].map((step) => (
              <div
                key={step}
                className={`h-1 md:h-1.5 rounded-full transition-all duration-500 ${
                  step <= 6
                    ? 'bg-[#FF5E00] shadow-[0_0_10px_rgba(255,94,0,0.4)]'
                    : 'bg-white/5'
                }`}
              />
            ))}
          </div>
        </div>

        <form onSubmit={handleSave} className="space-y-5 md:space-y-8">
          
          {/* Bloc 1: Planning Logistique */}
          <div className="bg-[#050505] border-[0.5px] border-zinc-900 rounded-[20px] md:rounded-3xl p-3.5 sm:p-5 md:p-10 shadow-2xl relative overflow-hidden">
            <div className="flex items-center space-x-3 md:space-x-4 mb-4 md:mb-8">
              <div className="p-2 md:p-3 bg-[#FF5E00]/10 rounded-lg md:rounded-xl">
                <Plus className="w-5 h-5 md:w-6 md:h-6 text-[#FF5E00]" />
              </div>
              <h2 className="text-sm md:text-lg font-bold text-white uppercase tracking-tight">Planning Logistique</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5 md:gap-8">
              <div className="space-y-3.5 md:space-y-6 min-w-0">
                <div className="min-w-0">
                  <label className="block font-mono text-[9px] md:text-[10px] text-zinc-500 uppercase tracking-[0.2em] mb-1 md:mb-3 truncate">Deadline Candidatures</label>
                  <input 
                    type="date" 
                    value={applicationDeadline}
                    onChange={(e) => setApplicationDeadline(e.target.value)}
                    className="w-full max-w-full box-border min-w-0 bg-black/40 border border-zinc-900 rounded-xl px-2.5 py-2 sm:px-4 sm:py-3.5 md:px-5 md:py-4 focus:outline-none focus:border-[#FF5E00] transition-colors text-xs sm:text-sm md:text-base block"
                  />
                </div>
                <div className="min-w-0">
                  <label className="block font-mono text-[9px] md:text-[10px] text-zinc-500 uppercase tracking-[0.2em] mb-1 md:mb-3 truncate">Arrivée exposants (date)</label>
                  <input 
                    type="date" 
                    value={arrivalDate}
                    onChange={(e) => setArrivalDate(e.target.value)}
                    className="w-full max-w-full box-border min-w-0 bg-black/40 border border-zinc-900 rounded-xl px-2.5 py-2 sm:px-4 sm:py-3.5 md:px-5 md:py-4 focus:outline-none focus:border-[#FF5E00] transition-colors text-xs sm:text-sm md:text-base block"
                  />
                </div>
                <div className="min-w-0">
                  <label className="block font-mono text-[9px] md:text-[10px] text-zinc-500 uppercase tracking-[0.2em] mb-1 md:mb-3 truncate">Heure d'arrivée</label>
                  <input 
                    type="time" 
                    value={arrivalTime}
                    onChange={(e) => setArrivalTime(e.target.value)}
                    className="w-full max-w-full box-border min-w-0 bg-black/40 border border-zinc-900 rounded-xl px-2.5 py-2 sm:px-4 sm:py-3.5 md:px-5 md:py-4 focus:outline-none focus:border-[#FF5E00] transition-colors text-xs sm:text-sm md:text-base block"
                  />
                </div>
              </div>
              <div className="space-y-3.5 md:space-y-6 min-w-0">
                <div className="min-w-0">
                  <label className="block font-mono text-[9px] md:text-[10px] text-zinc-500 uppercase tracking-[0.2em] mb-1 md:mb-3 truncate">Fin installation</label>
                  <input 
                    type="time" 
                    value={setupEndTime}
                    onChange={(e) => setSetupEndTime(e.target.value)}
                    className="w-full max-w-full box-border min-w-0 bg-black/40 border border-zinc-900 rounded-xl px-2.5 py-2 sm:px-4 sm:py-3.5 md:px-5 md:py-4 focus:outline-none focus:border-[#FF5E00] transition-colors text-xs sm:text-sm md:text-base block"
                  />
                </div>
                <div className="min-w-0">
                  <label className="block font-mono text-[9px] md:text-[10px] text-zinc-500 uppercase tracking-[0.2em] mb-1 md:mb-3 truncate">Départ exposants (date)</label>
                  <input 
                    type="date" 
                    value={arrivalDate} 
                    onChange={(e) => setArrivalDate(e.target.value)}
                    className="w-full max-w-full box-border min-w-0 bg-black/40 border border-zinc-900 rounded-xl px-2.5 py-2 sm:px-4 sm:py-3.5 md:px-5 md:py-4 focus:outline-none focus:border-[#FF5E00] transition-colors text-xs sm:text-sm md:text-base block"
                  />
                </div>
                <div className="min-w-0">
                  <label className="block font-mono text-[9px] md:text-[10px] text-zinc-500 uppercase tracking-[0.2em] mb-1 md:mb-3 truncate">Départ exposants (heures)</label>
                  <input 
                    type="time" 
                    value={departureTime}
                    onChange={(e) => setDepartureTime(e.target.value)}
                    className="w-full max-w-full box-border min-w-0 bg-black/40 border border-zinc-900 rounded-xl px-2.5 py-2 sm:px-4 sm:py-3.5 md:px-5 md:py-4 focus:outline-none focus:border-[#FF5E00] transition-colors text-xs sm:text-sm md:text-base block"
                  />
                </div>
              </div>
            </div>
          </div>


          {/* Bloc 3: Infrastructure & Matériel */}
          <div className="bg-[#050505] border-[0.5px] border-zinc-900 rounded-[20px] md:rounded-3xl p-4 sm:p-6 md:p-10 shadow-2xl relative overflow-hidden">
            <div className="flex items-center space-x-3 md:space-x-4 mb-5 md:mb-8">
              <div className="p-2 md:p-3 bg-[#FF5E00]/10 rounded-lg md:rounded-xl">
                <Ruler className="w-5 h-5 md:w-6 md:h-6 text-[#FF5E00]" />
              </div>
              <h2 className="text-sm md:text-lg font-bold text-white uppercase tracking-tight">Infrastructure</h2>
            </div>

            <div>
              <label className="block font-mono text-[9px] md:text-[10px] text-zinc-500 uppercase tracking-[0.2em] mb-4 md:mb-6 leading-tight">Matériel fourni :</label>
              <div className="flex flex-wrap gap-2 md:gap-3">
                {['Tables fournies', 'Chaises fournies', 'Barnum fourni', 'Électricité incluse', 'Eau potable'].map((item) => {
                  const isSelected = equipmentList.includes(item);
                  return (
                    <button
                      key={item}
                      type="button"
                      onClick={() => {
                        if (isSelected) {
                          setEquipmentList(equipmentList.filter(i => i !== item));
                        } else {
                          setEquipmentList([...equipmentList, item]);
                        }
                      }}
                      className={`px-4 md:px-6 py-2 md:py-3 rounded-full border text-[9px] md:text-xs font-bold uppercase tracking-wider transition-all ${
                        isSelected 
                        ? 'bg-[#FF5E00] border-[#FF5E00] text-white shadow-[0_0_15px_rgba(255,94,0,0.4)]' 
                        : 'border-zinc-900 bg-transparent text-zinc-400 hover:border-zinc-700'
                      }`}
                    >
                      {item}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Bloc 4: Sécurité & Logistique Site */}
          <div className="bg-[#050505] border-[0.5px] border-zinc-900 rounded-[20px] md:rounded-3xl p-4 sm:p-6 md:p-10 shadow-2xl relative overflow-hidden">
            <div className="flex items-center space-x-3 md:space-x-4 mb-5 md:mb-8">
              <div className="p-2 md:p-3 bg-[#FF5E00]/10 rounded-lg md:rounded-xl">
                <ShieldCheck className="w-5 h-5 md:w-6 md:h-6 text-[#FF5E00]" />
              </div>
              <h2 className="text-sm md:text-lg font-bold text-white uppercase tracking-tight">Logistique Site</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-8">
              <div>
                <label className="block font-mono text-[9px] md:text-[10px] text-zinc-500 uppercase tracking-[0.2em] mb-2 md:mb-3">Parking Exposant</label>
                <select 
                  value={parkingOption}
                  onChange={(e) => setParkingOption(e.target.value)}
                  className="w-full bg-[#050505] border border-zinc-900 rounded-xl px-3 md:px-5 py-3 md:py-4 focus:outline-none focus:border-[#FF5E00] transition-colors appearance-none text-sm md:text-base cursor-pointer"
                >
                  <option value="" className="bg-[#050505]">Sélectionner...</option>
                  <option value="Sur place" className="bg-[#050505]">Sur place</option>
                  <option value="À proximité" className="bg-[#050505]">À proximité</option>
                  <option value="Derrière le stand" className="bg-[#050505]">Derrière le stand</option>
                </select>
              </div>

              <div>
                <label className="block font-mono text-[9px] md:text-[10px] text-zinc-500 uppercase tracking-[0.2em] mb-2 md:mb-3">Gardiennage</label>
                <select 
                  value={securityOption}
                  onChange={(e) => setSecurityOption(e.target.value)}
                  className="w-full bg-[#050505] border border-zinc-900 rounded-xl px-3 md:px-5 py-3 md:py-4 focus:outline-none focus:border-[#FF5E00] transition-colors appearance-none text-sm md:text-base cursor-pointer"
                >
                  <option value="" className="bg-[#050505]">Sélectionner...</option>
                  <option value="Nocturne inclus" className="bg-[#050505]">Nocturne inclus</option>
                  <option value="Non assuré" className="bg-[#050505]">Non assuré</option>
                </select>
              </div>

              <div className="md:col-span-2">
                <label className="block font-mono text-[9px] md:text-[10px] text-zinc-500 uppercase tracking-[0.2em] mb-2 md:mb-3">Gestion des déchets</label>
                <select 
                  value={wasteManagement}
                  onChange={(e) => setWasteManagement(e.target.value)}
                  className="w-full bg-[#050505] border border-zinc-900 rounded-xl px-3 md:px-5 py-3 md:py-4 focus:outline-none focus:border-[#FF5E00] transition-colors appearance-none text-sm md:text-base cursor-pointer"
                >
                  <option value="" className="bg-[#050505]">Sélectionner...</option>
                  <option value="Bennes dispo" className="bg-[#050505]">Bennes dispo</option>
                  <option value="Par l'exposant" className="bg-[#050505]">Par l'exposant</option>
                </select>
              </div>
            </div>
          </div>

          {/* Bloc 5: Eau & Fluides */}
          <div className="bg-[#050505] border-[0.5px] border-zinc-900 rounded-[20px] md:rounded-3xl p-4 sm:p-6 md:p-10 shadow-2xl relative overflow-hidden">
            <div className="flex items-center space-x-3 md:space-x-4 mb-5 md:mb-8">
              <div className="p-2 md:p-3 bg-[#FF5E00]/10 rounded-lg md:rounded-xl">
                <Droplet className="w-5 h-5 md:w-6 md:h-6 text-[#FF5E00]" />
              </div>
              <h2 className="text-sm md:text-lg font-bold text-white uppercase tracking-tight">Eau & Fluides</h2>
            </div>

            <div className="space-y-5 md:space-y-6">
               <div className="flex items-center justify-between p-4 md:p-5 bg-black/40 border-[0.5px] border-zinc-900 rounded-xl">
                 <div>
                   <h3 className="font-bold text-[10px] md:text-xs uppercase tracking-wider text-white mb-1">Accès Eau Potable</h3>
                   <p className="text-[9px] md:text-[10px] uppercase font-mono text-zinc-500">Rampes accessibles ?</p>
                 </div>
                 <button 
                  type="button" 
                  onClick={() => setHasDrinkingWater(!hasDrinkingWater)}
                  className={`w-12 md:w-14 h-6 md:h-7 rounded-full transition-colors relative ${hasDrinkingWater ? 'bg-[#FF5E00]' : 'bg-zinc-800'}`}
                 >
                   <div className={`w-4 h-4 md:w-5 md:h-5 rounded-full bg-white absolute top-1 transition-transform ${hasDrinkingWater ? 'translate-x-7 md:translate-x-8' : 'translate-x-1'}`} />
                 </button>
               </div>

               <div>
                <label className="block font-mono text-[9px] md:text-[10px] text-zinc-500 uppercase tracking-[0.2em] mb-2">Eaux usées</label>
                <input 
                  type="text" 
                  value={wastewaterDetails}
                  onChange={(e) => setWastewaterDetails(e.target.value)}
                  placeholder="Instructions..."
                  className="w-full px-3 md:px-5 py-3 md:py-4 bg-black/40 border border-zinc-900 rounded-xl text-white placeholder-zinc-700 focus:outline-none focus:border-[#FF5E00] transition-colors text-sm md:text-base"
                />
              </div>
            </div>
          </div>

          {/* Bloc 6: Documents Techniques */}
          <div className="bg-[#050505] border-[0.5px] border-zinc-900 rounded-[20px] md:rounded-3xl p-4 sm:p-6 md:p-10 shadow-2xl relative overflow-hidden text-center">
             <div className="flex items-center justify-center space-x-3 md:space-x-4 mb-5 md:mb-8">
              <div className="p-2 md:p-3 bg-[#FF5E00]/10 rounded-lg md:rounded-xl">
                <UploadCloud className="w-5 h-5 md:w-6 md:h-6 text-[#FF5E00]" />
              </div>
              <h2 className="text-base md:text-2xl font-black text-white uppercase tracking-tighter">Documents</h2>
            </div>

            <div className="text-left bg-black/40 p-4 md:p-6 rounded-2xl border border-white/5 space-y-4">
              <DocumentDropzone 
                label="Documents Techniques"
                storagePath={`festivals/${sessionStorage.getItem('activeFestivalId') || user?.uid}/docs`}
                accept="application/pdf,image/*"
                onUploadComplete={(url) => console.log("Uploaded:", url)}
                description="Règlement, plan PDF, chartes..."
              />
            </div>
            <p className="font-mono text-[8px] md:text-[9px] text-zinc-600 uppercase tracking-widest mt-4 md:mt-6 leading-tight">
              Documents envoyés aux stands validés.
            </p>
          </div>

          {/* Coordination Bottom */}
          <div className="bg-white/5 border border-white/10 rounded-[24px] md:rounded-3xl p-6 md:p-8 mt-6">
            <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
              <button type="button" onClick={() => navigate(-1)} className="w-full md:w-auto px-8 py-4 rounded-xl md:rounded-2xl border border-white/10 text-white font-bold hover:bg-white/10 transition-all text-sm">Précédent</button>
              <p className="text-[10px] md:text-xs text-gray-500 font-medium text-center hidden md:block">Le plan du village sera configuré à l'étape suivante.</p>
              <button type="submit" disabled={isSaving} className={`w-full md:w-auto bg-white text-black font-black py-4 px-10 md:px-12 rounded-xl md:rounded-2xl hover:bg-[#ff5e00] hover:text-white transition-all active:scale-95 disabled:opacity-50 flex items-center justify-center gap-2 cursor-pointer`}>
                {isSaving ? 'ENREGISTREMENT...' : 'CONTINUER'}
                {!isSaving && <ArrowRight className="w-5 h-5" />}
              </button>
            </div>
          </div>

        </form>
      </main>
    </div>
  );
}
